import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from "@mui/material";
import { Home, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const formatEur = (n) => `€${Number(n).toFixed(2)}`;

const HOME_FEE = 2; // €2 for home pickup/delivery

export function RegularOptionsModal({
  open,
  onClose,
  basePrice = 5,
  onConfirm,
}) {
  const { t } = useTranslation();
  
  const [pickup, setPickup] = useState("center"); // "home" or "center"
  const [delivery, setDelivery] = useState("center"); // "home" or "center"

  const handleClose = () => {
    setPickup("center");
    setDelivery("center");
    onClose();
  };

  const calculatePrice = () => {
    let price = basePrice;
    if (pickup === "home") price += HOME_FEE;
    if (delivery === "home") price += HOME_FEE;
    return price;
  };

  const handleConfirm = () => {
    const finalPrice = calculatePrice();
    
    onConfirm?.({
      pickup,
      delivery,
      price: finalPrice,
    });

    handleClose();
  };

  const totalPrice = calculatePrice();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t("delivery.regularOptions") || "Regular Delivery Options"}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Pickup Selection */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("delivery.pickupLocation") || "Pickup Location"}
            </Typography>
            <ToggleButtonGroup
              value={pickup}
              exclusive
              onChange={(e, newValue) => newValue && setPickup(newValue)}
              fullWidth
              sx={{ display: 'flex', gap: 1 }}
            >
              <ToggleButton 
                value="center" 
                sx={{ 
                  flex: 1,
                  flexDirection: 'column',
                  py: 2,
                  gap: 1,
                  border: '1px solid',
                  borderColor: pickup === 'center' ? 'primary.main' : 'divider',
                  bgcolor: pickup === 'center' ? 'primary.50' : 'transparent',
                }}
              >
                <Building2 size={24} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t("delivery.parcelCenter") || "Parcel Center"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("delivery.free") || "Free"}
                  </Typography>
                </Box>
              </ToggleButton>
              
              <ToggleButton 
                value="home" 
                sx={{ 
                  flex: 1,
                  flexDirection: 'column',
                  py: 2,
                  gap: 1,
                  border: '1px solid',
                  borderColor: pickup === 'home' ? 'primary.main' : 'divider',
                  bgcolor: pickup === 'home' ? 'primary.50' : 'transparent',
                }}
              >
                <Home size={24} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t("delivery.homePickup") || "Home Pickup"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    +{formatEur(HOME_FEE)}
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          {/* Delivery Selection */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("delivery.deliveryLocation") || "Delivery Location"}
            </Typography>
            <ToggleButtonGroup
              value={delivery}
              exclusive
              onChange={(e, newValue) => newValue && setDelivery(newValue)}
              fullWidth
              sx={{ display: 'flex', gap: 1 }}
            >
              <ToggleButton 
                value="center" 
                sx={{ 
                  flex: 1,
                  flexDirection: 'column',
                  py: 2,
                  gap: 1,
                  border: '1px solid',
                  borderColor: delivery === 'center' ? 'primary.main' : 'divider',
                  bgcolor: delivery === 'center' ? 'primary.50' : 'transparent',
                }}
              >
                <Building2 size={24} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t("delivery.parcelCenter") || "Parcel Center"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("delivery.free") || "Free"}
                  </Typography>
                </Box>
              </ToggleButton>
              
              <ToggleButton 
                value="home" 
                sx={{ 
                  flex: 1,
                  flexDirection: 'column',
                  py: 2,
                  gap: 1,
                  border: '1px solid',
                  borderColor: delivery === 'home' ? 'primary.main' : 'divider',
                  bgcolor: delivery === 'home' ? 'primary.50' : 'transparent',
                }}
              >
                <Home size={24} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t("delivery.homeDelivery") || "Home Delivery"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    +{formatEur(HOME_FEE)}
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Price Summary */}
          <Card sx={{ bgcolor: 'primary.50', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("delivery.totalPrice") || "Total Price"}
                </Typography>
                <Typography variant="h6" fontWeight={900} color="primary">
                  {formatEur(totalPrice)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          {t("common.cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
        >
          {t("common.confirm") || "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}